import { useCallback, useRef, useState } from "react";
import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import QrCodeScannerOutlinedIcon from "@mui/icons-material/QrCodeScannerOutlined";
import SyncAltOutlinedIcon from "@mui/icons-material/SyncAltOutlined";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { brandingInfo } from "features/server/brandingSlice";
import { themes } from "theme/themes";
import {
  useGetMarketplaceService,
  // useGetMarketplacePagedService,
  useRequestMarketplaceService,
} from "subModule/src/services/marketplace/useMarketplace";
import type { MarketplaceModule } from "subModule/src/services/marketplace/interface";

/** Matches backend MARKETPLACE_STATUS */
const MARKETPLACE_STATUS = {
  ACTIVE: 1,
  INACTIVE: 2,
  REQUESTED: 3,
} as const;

type ModuleVisual = {
  icon: React.ReactNode;
  bg: string;
  color: string;
};

const resolveModuleKey = (item: MarketplaceModule) => Number(item.id ?? 0);

const getModuleVisual = (moduleId: number, title?: string): ModuleVisual => {
  if (moduleId === 10 || (title || "").toLowerCase().includes("whatsapp")) {
    return {
      icon: <WhatsAppIcon sx={{ fontSize: 21 }} />,
      bg: "#E8F8EF",
      color: "#1FA855",
    };
  }
  if (
    moduleId === 20 ||
    (title || "").toLowerCase().includes("payment") ||
    (title || "").toLowerCase().includes("gateway")
  ) {
    return {
      icon: <CreditCardOutlinedIcon sx={{ fontSize: 21 }} />,
      bg: "#F3E8FF",
      color: "#7C3AED",
    };
  }
  if (moduleId === 30 || (title || "").toLowerCase().includes("vin")) {
    return {
      icon: <QrCodeScannerOutlinedIcon sx={{ fontSize: 21 }} />,
      bg: "#E8F1FF",
      color: "#2563EB",
    };
  }
  if (
    moduleId === 40 ||
    (title || "").toLowerCase().includes("zoho") ||
    (title || "").toLowerCase().includes("sync")
  ) {
    return {
      icon: <SyncAltOutlinedIcon sx={{ fontSize: 21 }} />,
      bg: "#FFF4E5",
      color: "#EA580C",
    };
  }
  if (
    moduleId === 50 ||
    (title || "").toLowerCase().includes("white") ||
    (title || "").toLowerCase().includes("label")
  ) {
    return {
      icon: <BrushOutlinedIcon sx={{ fontSize: 21 }} />,
      bg: "#FCE7F3",
      color: "#DB2777",
    };
  }
  if (
    moduleId === 60 ||
    (title || "").toLowerCase().includes("llm") ||
    (title || "").toLowerCase().includes("chatbot") ||
    (title || "").toLowerCase().includes("ai")
  ) {
    return {
      icon: <SmartToyOutlinedIcon sx={{ fontSize: 21 }} />,
      bg: "#EEF2FF",
      color: "#4F46E5",
    };
  }
  if (
    moduleId === 70 ||
    (title || "").toLowerCase().includes("fms") ||
    (title || "").toLowerCase().includes("fleet")
  ) {
    return {
      icon: <DirectionsCarFilledOutlinedIcon sx={{ fontSize: 21 }} />,
      bg: "#ECFDF5",
      color: "#059669",
    };
  }
  return {
    icon: <ExtensionOutlinedIcon sx={{ fontSize: 21 }} />,
    bg: "#F3F4F6",
    color: "#4B5563",
  };
};

export default function Marketplace() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar" || i18n.language === "ur";
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];

  const primary = themeColors?.primary || "#4C0055";
  const secondary = themeColors?.secondary || "#FC5F3A";

  const [requestingModule, setRequestingModule] = useState<number | null>(null);
  const [localRequested, setLocalRequested] = useState<Set<number>>(
    () => new Set(),
  );
  const pendingModuleRef = useRef<number | null>(null);

  const {
    modules,
    isFetching: isModulesFetching,
    refetch: refetchModules,
  } = useGetMarketplaceService();

  // /api/marketplace/paged disabled — use modules[].status instead
  // const {
  //   requestedModuleIds,
  //   isFetching: isPagedFetching,
  //   refetch: refetchPaged,
  // } = useGetMarketplacePagedService({ page: 1, size: 100 });

  const refreshMarketplace = useCallback(() => {
    return refetchModules();
  }, [refetchModules]);

  const resolveStatus = useCallback(
    (item: MarketplaceModule) => {
      const moduleId = resolveModuleKey(item);
      if (moduleId > 0 && localRequested.has(moduleId)) {
        return MARKETPLACE_STATUS.REQUESTED;
      }
      return Number(item.status ?? 0);
    },
    [localRequested],
  );

  const { onRequestModule } = useRequestMarketplaceService({
    onSuccess: async (res) => {
      const moduleId = pendingModuleRef.current;
      pendingModuleRef.current = null;
      setRequestingModule(null);
      if (res?.success) {
        if (moduleId != null) {
          setLocalRequested((prev) => new Set(prev).add(moduleId));
        }
        enqueueSnackbar(
          <AlertMessages
            statusCode={res.status}
            text={t("marketplaceRequestSent")}
          />,
          { variant: "success" },
        );
        await refreshMarketplace();
      } else {
        enqueueSnackbar(
          <AlertMessages text={t(res?.message || "Something went wrong")} />,
          { variant: "error" },
        );
      }
    },
    onError: (err: any) => {
      pendingModuleRef.current = null;
      setRequestingModule(null);
      enqueueSnackbar(
        <AlertMessages
          text={err?.message || err?.data?.message || t("Something went wrong")}
        />,
        { variant: "error" },
      );
    },
  });

  const handleRequest = useCallback(
    async (item: MarketplaceModule) => {
      const moduleId = resolveModuleKey(item);
      const status = resolveStatus(item);
      // Request is available for inactive modules (button is hidden for active / already requested)
      if (!moduleId || status !== MARKETPLACE_STATUS.INACTIVE) return;

      pendingModuleRef.current = moduleId;
      setRequestingModule(moduleId);
      await onRequestModule({ module: moduleId });
    },
    [onRequestModule, resolveStatus],
  );

  const isLoading = isModulesFetching && !modules.length;

  return (
    <Box
      sx={{
        width: "100%",
        textAlign: isRtl ? "right" : "left",
        direction: isRtl ? "rtl" : "ltr",
        "& .MuiTypography-root": { textAlign: isRtl ? "right" : "left" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: { xs: 2, md: 2.5 },
          py: 2,
          minHeight: 56,
          borderRadius: "10px",
          background: primary,
          color: "#fff",
          mb: 2.5,
          textAlign: isRtl ? "right" : "left",
          flexDirection: isRtl ? "row-reverse" : "row",
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255,255,255,0.14)",
            flexShrink: 0,
          }}
        >
          <StorefrontOutlinedIcon sx={{ fontSize: 21, color: "#fff" }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1, textAlign: isRtl ? "right" : "left" }}>
          <Typography
            className="font-Saira"
            sx={{
              fontWeight: 700,
              fontSize: { xs: 16, md: 18 },
              lineHeight: 1.2,
              color: "#fff",
              textAlign: isRtl ? "right" : "left",
            }}
          >
            {t("marketplace")}
          </Typography>
          <Typography
            className="font-Saira"
            sx={{
              mt: 0.25,
              fontWeight: 400,
              fontSize: { xs: 11, md: 12.5 },
              lineHeight: 1.3,
              color: "rgba(255,255,255,0.88)",
              textAlign: isRtl ? "right" : "left",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {t("marketplaceSubtitle")}
          </Typography>
        </Box>
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 220,
          }}
        >
          <CircularProgress sx={{ color: primary }} />
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {modules.map((item, index) => {
            const moduleId = resolveModuleKey(item);
            const status = resolveStatus(item);
            const isActive = status === MARKETPLACE_STATUS.ACTIVE;
            const isInactive = status === MARKETPLACE_STATUS.INACTIVE;
            const isRequested = status === MARKETPLACE_STATUS.REQUESTED;
            const visual = getModuleVisual(moduleId, item.title);
            const title = t(`marketplaceModule${moduleId}Title`, {
              defaultValue: item.title || t("marketplaceModule"),
            });
            const description = t(`marketplaceModule${moduleId}Description`, {
              defaultValue: item.description || "",
            });
            const busy = requestingModule === moduleId;

            const statusChip = isRequested
              ? {
                  label: t("requested"),
                  background: "#E8F8EF",
                  color: "#178A45",
                }
              : isInactive
                ? {
                    label: t("inactive"),
                    background: "#F3F4F6",
                    color: "#6B7280",
                  }
                : isActive
                  ? {
                      label: t("active"),
                      background: "#E8F1FF",
                      color: "#2563EB",
                    }
                  : null;

            return (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={`${moduleId || item.title}-${index}`}
              >
                <Box
                  sx={{
                    height: "100%",
                    background: "#fff",
                    borderRadius: "12px",
                    border: "1px solid #EEF0F3",
                    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
                    p: 2.25,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    minHeight: 168,
                    textAlign: isRtl ? "right" : "left",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      width: "100%",
                      gap: 1,
                      mb: 1.25,
                      flexDirection: isRtl ? "row-reverse" : "row",
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        background: visual.bg,
                        color: visual.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {visual.icon}
                    </Box>
                    {statusChip ? (
                      <Chip
                        label={statusChip.label}
                        size="small"
                        sx={{
                          height: 24,
                          borderRadius: "999px",
                          background: statusChip.background,
                          color: statusChip.color,
                          fontFamily: "Saira, sans-serif",
                          fontWeight: 600,
                          fontSize: 11,
                          "& .MuiChip-label": { px: 1.25 },
                        }}
                      />
                    ) : null}
                  </Box>

                  <Typography
                    className="font-Saira"
                    sx={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: "#111827",
                      lineHeight: 1.3,
                      mb: 0.75,
                      textAlign: isRtl ? "right" : "left",
                      width: "100%",
                    }}
                  >
                    {title}
                  </Typography>

                  <Typography
                    className="font-Saira"
                    sx={{
                      fontWeight: 400,
                      fontSize: 13.5,
                      color: "#6B7280",
                      lineHeight: 1.5,
                      flexGrow: 1,
                      mb: 1.75,
                      textAlign: isRtl ? "right" : "left",
                      width: "100%",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {description}
                  </Typography>

                  {!isActive && !isRequested ? (
                    <ButtonBase
                      disabled={busy || !moduleId}
                      onClick={() => handleRequest(item)}
                      sx={{
                        alignSelf: isRtl ? "flex-end" : "flex-start",
                        color: secondary,
                        fontFamily: "Saira, sans-serif",
                        fontWeight: 600,
                        fontSize: 13,
                        gap: 0.5,
                        flexDirection: isRtl ? "row-reverse" : "row",
                        opacity: busy ? 0.7 : 1,
                        cursor: "pointer",
                        "&.Mui-disabled": {
                          color: secondary,
                          opacity: 0.45,
                        },
                      }}
                    >
                      {busy ? (
                        <CircularProgress size={14} sx={{ color: secondary }} />
                      ) : null}
                      <span>{t("requestMarketplace")}</span>
                      <ArrowForwardIcon
                        sx={{
                          fontSize: 15,
                          transform: isRtl ? "scaleX(-1)" : undefined,
                        }}
                      />
                    </ButtonBase>
                  ) : isRequested ? (
                    <Typography
                      className="font-Saira"
                      sx={{
                        alignSelf: isRtl ? "flex-end" : "flex-start",
                        color: "#178A45",
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      {t("requestSent")}
                    </Typography>
                  ) : null}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      {!isLoading && !modules.length ? (
        <Box
          sx={{
            textAlign: isRtl ? "right" : "left",
            py: 8,
            color: "#6B7280",
            fontFamily: "Saira, sans-serif",
          }}
        >
          {t("noMarketplaceModules")}
        </Box>
      ) : null}
    </Box>
  );
}
